/**
 * Local Delivery Cart UI/event orchestration controller.
 * Owns DOM listener wiring and UI state transitions for method/timeslot selection.
 */
(function () {
  'use strict';

  function createUiController(ctx) {
    ctx = ctx || {};
    var deps = ctx.deps || {};
    var EXPRESS = deps.EXPRESS || window.WF_LD_EXPRESS_CART;
    var STORAGE = deps.STORAGE || window.WF_LD_STORAGE;
    var wrap = ctx.dom && ctx.dom.wrap ? ctx.dom.wrap : null;
    var disposers = [];
    var dateHintTimer = null;

    function getEl(id) {
      if (ctx.dom && typeof ctx.dom.getEl === 'function') return ctx.dom.getEl(id);
      return document.getElementById(id);
    }

    function getConfig() {
      return ctx.getters && ctx.getters.getConfig ? ctx.getters.getConfig() : null;
    }

    function getMatchedOccasion() {
      return ctx.getters && ctx.getters.getMatchedOccasion ? ctx.getters.getMatchedOccasion() : null;
    }

    function getDatePicker() {
      return ctx.getters && ctx.getters.getDatePicker ? ctx.getters.getDatePicker() : null;
    }

    function getDateSelected() {
      return ctx.getters && ctx.getters.getDateSelected ? !!ctx.getters.getDateSelected() : false;
    }

    function setDateSelected(next) {
      if (ctx.setters && ctx.setters.setDateSelected) ctx.setters.setDateSelected(!!next);
    }

    function getActiveTimeslotType() {
      if (ctx.getters && ctx.getters.getActiveTimeslotType) return ctx.getters.getActiveTimeslotType();
      return 'standard';
    }

    function setActiveTimeslotType(next) {
      if (ctx.setters && ctx.setters.setActiveTimeslotType) ctx.setters.setActiveTimeslotType(next);
    }

    function getActiveAdvanceLeadDays() {
      if (ctx.getters && ctx.getters.getActiveAdvanceLeadDays) return ctx.getters.getActiveAdvanceLeadDays() || 0;
      return 0;
    }

    function setActiveAdvanceLeadDays(next) {
      if (ctx.setters && ctx.setters.setActiveAdvanceLeadDays) ctx.setters.setActiveAdvanceLeadDays(next || 0);
    }

    function setExpressSelectionError(next) {
      if (ctx.setters && ctx.setters.setExpressSelectionError) ctx.setters.setExpressSelectionError(next || '');
    }

    function getMethodDelivery() {
      return getEl('wf-ld-method-delivery');
    }

    function getMethodCollection() {
      return getEl('wf-ld-method-collection');
    }

    function getTimeslotTypeCards() {
      return wrap ? wrap.querySelectorAll('.wf-ld-timeslot-type-cards .wf-ld-method-card') : [];
    }

    function getMethodCards() {
      return wrap ? wrap.querySelectorAll('.wf-ld-method-card[data-method]') : [];
    }

    function updateExpressCardLabel() {
      if (ctx.actions && typeof ctx.actions.updateExpressCardLabel === 'function') {
        ctx.actions.updateExpressCardLabel();
      }
    }

    function updateStandardCardLabel() {
      if (ctx.actions && typeof ctx.actions.updateStandardCardLabel === 'function') {
        ctx.actions.updateStandardCardLabel();
      }
    }

    function validateAndBlockCheckout() {
      if (ctx.actions && typeof ctx.actions.validateAndBlockCheckout === 'function') {
        ctx.actions.validateAndBlockCheckout();
      }
    }

    function populateTimeslots(dateIso) {
      if (ctx.actions && typeof ctx.actions.populateTimeslots === 'function') {
        return ctx.actions.populateTimeslots(dateIso);
      }
    }

    function fetchAvailabilityAndPopulate(dateIso) {
      if (ctx.actions && typeof ctx.actions.fetchAvailabilityAndPopulate === 'function') {
        return ctx.actions.fetchAvailabilityAndPopulate(dateIso);
      }
    }

    function updateDateHints(dateIso) {
      if (ctx.actions && typeof ctx.actions.updateDateHints === 'function') {
        ctx.actions.updateDateHints(dateIso);
      }
    }

    function onChange() {
      if (ctx.actions && typeof ctx.actions.onChange === 'function') {
        ctx.actions.onChange();
      }
    }

    function updateRecipientVisibility() {
      if (ctx.actions && typeof ctx.actions.updateRecipientVisibility === 'function') {
        ctx.actions.updateRecipientVisibility();
      }
    }

    function runDatePickerUpdate() {
      var datePicker = getDatePicker();
      if (datePicker && datePicker.updateOpts) datePicker.updateOpts({});
    }

    function saveStateSnapshot() {
      if (!STORAGE || typeof STORAGE.save !== 'function') return;
      if (!ctx.actions || typeof ctx.actions.collectState !== 'function') return;
      STORAGE.save(ctx.actions.collectState());
    }

    function on(el, eventName, handler, options) {
      if (!el || !eventName || !handler) return;
      if (typeof el.addEventListener !== 'function' || typeof el.removeEventListener !== 'function') return;
      el.addEventListener(eventName, handler, options);
      disposers.push(function () {
        el.removeEventListener(eventName, handler, options);
      });
    }

    function syncCardActive() {
      var methodDelivery = getMethodDelivery();
      var methodCollection = getMethodCollection();
      var methodCards = getMethodCards();
      methodCards.forEach(function (card) {
        var method = card.getAttribute('data-method');
        var isActive = (method === 'delivery' && methodDelivery && methodDelivery.checked) ||
          (method === 'collection' && methodCollection && methodCollection.checked);
        card.classList.toggle('active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    var lastExpressToastKey = '';
    var pendingExpressApplyKey = '';

    // Lightweight, dependency-free toast so customers get immediate feedback that the
    // express delivery fee is being applied (avoids a silent/delayed-feeling state).
    function wfLdShowToast(message) {
      if (!message) return;
      try {
        var host = document.getElementById('wf-ld-toast-host');
        if (!host) {
          host = document.createElement('div');
          host.id = 'wf-ld-toast-host';
          host.setAttribute('aria-live', 'polite');
          host.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99999;display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none;max-width:92vw;';
          document.body.appendChild(host);
        }
        var toast = document.createElement('div');
        toast.className = 'wf-ld-toast';
        toast.textContent = message;
        toast.style.cssText = 'background:#282928;color:#fff;padding:12px 18px;border-radius:8px;font-size:14px;line-height:1.4;box-shadow:0 6px 24px rgba(0,0,0,.18);opacity:0;transform:translateY(8px);transition:opacity .25s ease,transform .25s ease;pointer-events:auto;text-align:center;';
        host.appendChild(toast);
        requestAnimationFrame(function () { toast.style.opacity = '1'; toast.style.transform = 'translateY(0)'; });
        setTimeout(function () {
          toast.style.opacity = '0';
          toast.style.transform = 'translateY(8px)';
          setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 300);
        }, 4000);
      } catch (e) {}
    }
    window.WF_LD_SHOW_TOAST = wfLdShowToast;

    function clearFeeLine() {
      var clearPromise = EXPRESS && EXPRESS.removeAllFeeProducts
        ? EXPRESS.removeAllFeeProducts()
        : (EXPRESS && EXPRESS.removeAllExpressProducts
          ? EXPRESS.removeAllExpressProducts()
          : Promise.resolve());
      return clearPromise.then(function () {
        if (EXPRESS && EXPRESS.refreshCartTotals) EXPRESS.refreshCartTotals();
        if (EXPRESS && EXPRESS.setFeeChargeVisible) EXPRESS.setFeeChargeVisible(false);
        else if (EXPRESS && EXPRESS.setExpressChargeVisible) EXPRESS.setExpressChargeVisible(false);
        if (ctx.actions && typeof ctx.actions.setDeliveryFeeRow === 'function') {
          ctx.actions.setDeliveryFeeRow('Delivery Fee', null);
        }
        lastExpressToastKey = '';
        pendingExpressApplyKey = '';
      });
    }

    function applyFeeFromMeta(meta, mode, fallbackLabel) {
      if (!meta || !meta.rawLabel) {
        setExpressSelectionError('');
        return clearFeeLine();
      }
      var selectedVariant = meta.variantId ? String(meta.variantId) : '';
      if (!selectedVariant) {
        if (mode === 'express' || meta.tierId) {
          setExpressSelectionError(mode === 'express'
            ? 'Express fee is not configured for this timeslot. Please choose another timeslot.'
            : 'Delivery fee is not configured for this timeslot. Please choose another timeslot.');
          return clearFeeLine().then(function () {
            updateExpressCardLabel();
            updateStandardCardLabel();
          });
        }
        setExpressSelectionError('');
        return clearFeeLine();
      }

      setExpressSelectionError('');
      var feeLabel = meta.feeLabel || fallbackLabel || 'Delivery Fee';
      // Immediate feedback the moment an express slot is chosen, before the async fee add resolves.
      if (mode === 'express') {
        var applyKey = String(meta.variantId || '') + '|' + String(meta.rawLabel || '');
        if (applyKey !== lastExpressToastKey) {
          lastExpressToastKey = applyKey;
          pendingExpressApplyKey = applyKey;
          wfLdShowToast('Applying express delivery fee…');
        }
      }
      return EXPRESS.addFeeProduct({
        variantId: selectedVariant,
        timeslot: meta.rawLabel,
        mode: mode,
        label: feeLabel,
      }).then(function (result) {
        var applied = !!(result && (result.present === true || result.item));
        if (!applied) {
          setExpressSelectionError(mode === 'express'
            ? 'Unable to apply express fee for this timeslot. Please try another slot.'
            : 'Unable to apply delivery fee for this timeslot. Please try another slot.');
          return clearFeeLine().then(function () {
            updateExpressCardLabel();
            updateStandardCardLabel();
            validateAndBlockCheckout();
          });
        }
        if (EXPRESS && EXPRESS.refreshCartTotals) EXPRESS.refreshCartTotals();
        if (EXPRESS && EXPRESS.setFeeChargeVisible) EXPRESS.setFeeChargeVisible(true);
        if (ctx.actions && typeof ctx.actions.setDeliveryFeeRow === 'function') {
          ctx.actions.setDeliveryFeeRow(feeLabel, meta.amount);
        }
        if (mode === 'express') {
          var confirmKey = String(meta.variantId || '') + '|' + String(meta.rawLabel || '');
          if (confirmKey === pendingExpressApplyKey) {
            pendingExpressApplyKey = '';
            var amtTxt = (meta.amount != null && isFinite(meta.amount)) ? ('$' + Number(meta.amount).toFixed(2)) : null;
            wfLdShowToast(amtTxt
              ? ('Express delivery fee ' + amtTxt + ' added to your order.')
              : 'Express delivery fee added to your order.');
          }
        }
      }).catch(function () {
        setExpressSelectionError(mode === 'express'
          ? 'Unable to apply express fee. Please try again.'
          : 'Unable to apply delivery fee. Please try again.');
      });
    }

    function refreshAdvanceBookingState() {
      var nextLeadDays = 0;
      if (ctx.actions && typeof ctx.actions.computeActiveAdvanceLeadDays === 'function') {
        nextLeadDays = ctx.actions.computeActiveAdvanceLeadDays() || 0;
      }
      setActiveAdvanceLeadDays(nextLeadDays);
      if (ctx.actions && typeof ctx.actions.applyDynamicDateBounds === 'function') {
        ctx.actions.applyDynamicDateBounds();
      }
      validateAndBlockCheckout();
    }

    function flashDateHint() {
      var hint = getEl('wf-ld-date-hint');
      if (!hint || getDateSelected()) return;
      hint.style.display = 'block';
      if (dateHintTimer) clearTimeout(dateHintTimer);
      dateHintTimer = setTimeout(function () { hint.style.display = 'none'; }, 3000);
    }

    function updateTimeslotLockState() {
      var stdSelect = getEl('wf-ld-timeslot');
      var expSelect = getEl('wf-ld-express-timeslot');
      var hint = getEl('wf-ld-date-hint');
      var dateSelected = getDateSelected();

      // Until a date is picked these controls are unusable; hide them instead of
      // greying so the form reads shorter (class overrides the inline display
      // toggles from updateTimeslotTypeVisibility/switchTimeslotType).
      ['wf-ld-timeslot-type-block', 'wf-ld-standard-slot-block', 'wf-ld-express-slot-block'].forEach(function (id) {
        var block = getEl(id);
        if (block && block.classList) block.classList.toggle('wf-ld-predate-hidden', !dateSelected);
      });

      getTimeslotTypeCards().forEach(function (card) {
        card.classList.toggle('wf-ld-locked', !dateSelected);
      });
      if (stdSelect) {
        stdSelect.classList.toggle('wf-ld-locked', !dateSelected);
        stdSelect.disabled = !dateSelected;
      }
      if (expSelect) {
        expSelect.classList.toggle('wf-ld-locked', !dateSelected);
        expSelect.disabled = !dateSelected;
      }
      if (hint && dateSelected) hint.style.display = 'none';
    }

    function syncExpressCartOnSlotSelect() {
      if (getActiveTimeslotType() !== 'express') return;
      var selectedMeta = ctx.actions && ctx.actions.getSelectedExpressSlotMeta
        ? ctx.actions.getSelectedExpressSlotMeta()
        : null;
      return applyFeeFromMeta(selectedMeta, 'express', 'Express Delivery').then(function () {
        updateExpressCardLabel();
        updateStandardCardLabel();
      });
    }

    function syncStandardCartOnSlotSelect() {
      if (getActiveTimeslotType() !== 'standard') return;
      var methodDelivery = getMethodDelivery();
      var methodIsDelivery = methodDelivery && methodDelivery.checked;
      if (!methodIsDelivery) {
        setExpressSelectionError('');
        clearFeeLine().then(function () {
          updateExpressCardLabel();
          updateStandardCardLabel();
        });
        return Promise.resolve();
      }
      var selectedMeta = ctx.actions && ctx.actions.getSelectedStandardSlotMeta
        ? ctx.actions.getSelectedStandardSlotMeta()
        : null;
      return applyFeeFromMeta(selectedMeta, 'delivery', 'Delivery Fee').then(function () {
        updateExpressCardLabel();
        updateStandardCardLabel();
      });
    }

    function syncFeeStateAfterTimeslotRefresh(summary) {
      var activeType = getActiveTimeslotType();
      var branch = activeType === 'express' ? (summary && summary.express) : (summary && summary.standard);
      if (!branch || !branch.hadPrevious) return Promise.resolve(false);
      if (!branch.changed && !branch.cleared) return Promise.resolve(false);
      var syncPromise = activeType === 'express'
        ? syncExpressCartOnSlotSelect()
        : syncStandardCartOnSlotSelect();
      return Promise.resolve(syncPromise).then(function () {
        validateAndBlockCheckout();
        return true;
      });
    }

    function switchTimeslotType(type) {
      if (type !== 'standard' && !getDateSelected()) return;
      setActiveTimeslotType(type);
      getTimeslotTypeCards().forEach(function (card) {
        var cardType = card.getAttribute('data-timeslot-type');
        var isActive = (cardType === type);
        card.classList.toggle('active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      var stdSlotBlock = getEl('wf-ld-standard-slot-block');
      var expSlotBlock = getEl('wf-ld-express-slot-block');
      if (stdSlotBlock) stdSlotBlock.style.display = (type === 'standard') ? 'block' : 'none';
      if (expSlotBlock) expSlotBlock.style.display = (type === 'express') ? 'block' : 'none';
      if (type !== 'express') syncStandardCartOnSlotSelect();
      refreshAdvanceBookingState();
      updateExpressCardLabel();
      updateStandardCardLabel();
    }

    function updateTimeslotTypeVisibility() {
      var methodDelivery = getMethodDelivery();
      var isDelivery = methodDelivery && methodDelivery.checked;
      var config = getConfig();
      var matchedOccasion = getMatchedOccasion();
      var hasExpress = config && config.express_timeslots && config.express_timeslots.length > 0;
      var occasionBlocksExpress = matchedOccasion && matchedOccasion.disable_express;
      var showExpress = isDelivery && hasExpress && !occasionBlocksExpress;
      var timeslotTypeBlock = getEl('wf-ld-timeslot-type-block');
      if (timeslotTypeBlock) timeslotTypeBlock.style.display = showExpress ? 'block' : 'none';
      if (!showExpress) switchTimeslotType('standard');
      updateTimeslotLockState();
      updateExpressCardLabel();
      updateStandardCardLabel();
    }

    function bindMethodCards() {
      var methodDelivery = getMethodDelivery();
      var methodCollection = getMethodCollection();
      getMethodCards().forEach(function (card) {
        on(card, 'click', function () {
          var method = card.getAttribute('data-method');
          var radio = (method === 'delivery') ? methodDelivery : methodCollection;
          if (radio) {
            radio.checked = true;
            syncCardActive();
            updateTimeslotTypeVisibility();
            refreshAdvanceBookingState();
            onChange();
            updateRecipientVisibility();
          }
        });
        on(card, 'keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });
      });
      syncCardActive();
    }

    function bindTimeslotTypeCards() {
      getTimeslotTypeCards().forEach(function (card) {
        on(card, 'click', function () {
          if (!getDateSelected()) {
            flashDateHint();
            return;
          }
          var type = card.getAttribute('data-timeslot-type');
          if (type) {
            switchTimeslotType(type);
            onChange();
          }
        });
        on(card, 'keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });
      });

      var stdSelectEl = getEl('wf-ld-timeslot');
      var expSelectEl = getEl('wf-ld-express-timeslot');
      on(stdSelectEl, 'mousedown', function (e) {
        if (!getDateSelected()) {
          e.preventDefault();
          flashDateHint();
        }
      });
      on(expSelectEl, 'mousedown', function (e) {
        if (!getDateSelected()) {
          e.preventDefault();
          flashDateHint();
        }
      });
    }

    function bindFormInputs() {
      var methodDelivery = getMethodDelivery();
      var methodCollection = getMethodCollection();
      var postalInput = getEl('wf-ld-recipient-postal');
      var dateEl = getEl('wf-ld-date');
      var timeslotEl = getEl('wf-ld-timeslot');
      var expressTimeslotEl = getEl('wf-ld-express-timeslot');
      var staleRefreshTimer = null;

      function refreshSelectedDateAvailability() {
        if (!ctx.getters || typeof ctx.getters.getSelectedDateIso !== 'function') return;
        var iso = ctx.getters.getSelectedDateIso();
        if (!iso) return;
        Promise.resolve(fetchAvailabilityAndPopulate(iso)).then(function (summary) {
          return syncFeeStateAfterTimeslotRefresh(summary);
        }).then(function () {
          validateAndBlockCheckout();
        });
      }

      function scheduleSelectedDateAvailabilityRefresh() {
        if (staleRefreshTimer) clearTimeout(staleRefreshTimer);
        staleRefreshTimer = setTimeout(function () {
          staleRefreshTimer = null;
          refreshSelectedDateAvailability();
        }, 120);
      }

      on(methodDelivery, 'change', function () {
        syncCardActive();
        updateTimeslotTypeVisibility();
        refreshAdvanceBookingState();
        runDatePickerUpdate();
        populateTimeslots();
        syncStandardCartOnSlotSelect();
        onChange();
        updateRecipientVisibility();
        updateExpressCardLabel();
        updateStandardCardLabel();
      });

      on(methodCollection, 'change', function () {
        syncCardActive();
        updateTimeslotTypeVisibility();
        refreshAdvanceBookingState();
        runDatePickerUpdate();
        populateTimeslots();
        onChange();
        updateRecipientVisibility();
        setExpressSelectionError('');
        clearFeeLine();
        updateExpressCardLabel();
        updateStandardCardLabel();
      });

      function maybeValidatePostalFromInput() {
        if (!postalInput) return;
        var code = String(postalInput.value || '').trim();
        if (code.length === 6 && ctx.actions && typeof ctx.actions.validatePostalServer === 'function') {
          ctx.actions.validatePostalServer(code);
        }
      }

      if (postalInput) {
        on(postalInput, 'input', function () {
          if (ctx.actions && typeof ctx.actions.setPostalServerValid === 'function') {
            ctx.actions.setPostalServerValid(null);
          }
          if (ctx.actions && typeof ctx.actions.clearAddressConsistencyHint === 'function') {
            ctx.actions.clearAddressConsistencyHint();
          }
          maybeValidatePostalFromInput();
          onChange();
        });
        on(postalInput, 'blur', function () {
          maybeValidatePostalFromInput();
          if (ctx.actions && typeof ctx.actions.scheduleAddressConsistencyCheck === 'function') {
            ctx.actions.scheduleAddressConsistencyCheck();
          }
          onChange();
        });
      }

      if (dateEl && !getDatePicker()) {
        on(dateEl, 'change', function () {
          var value = dateEl.value || '';
          dateEl.setAttribute('data-value', value);
          setDateSelected(!!value);
          updateDateHints(value);
          Promise.resolve(fetchAvailabilityAndPopulate(value)).then(function (summary) {
            return syncFeeStateAfterTimeslotRefresh(summary);
          });
          updateTimeslotLockState();
          onChange();
        });
      }

      if (timeslotEl) {
        on(timeslotEl, 'change', function () {
          syncStandardCartOnSlotSelect();
          updateStandardCardLabel();
          onChange();
        });
      }

      if (expressTimeslotEl) {
        on(expressTimeslotEl, 'change', function () {
          syncExpressCartOnSlotSelect();
          updateExpressCardLabel();
          onChange();
        });
      }

      if (wrap) {
        var contactFields = wrap.querySelectorAll(
          'input[name^="attributes[recipient_"], input[name^="attributes[sender_"]'
        );
        function isAddressGateFieldName(name) {
          return name === 'attributes[recipient_address1]' ||
            name === 'attributes[recipient_address2]' ||
            name === 'attributes[recipient_postal]';
        }

        contactFields.forEach(function (inp) {
          on(inp, 'input', function () {
            var name = String((inp && (inp.name || (inp.getAttribute && inp.getAttribute('name')))) || '');
            if (
              (name === 'attributes[recipient_address1]' || name === 'attributes[recipient_postal]') &&
              ctx.actions &&
              typeof ctx.actions.clearAddressConsistencyHint === 'function' &&
              !(typeof ctx.actions.isRecipientAutocompleteApplying === 'function' && ctx.actions.isRecipientAutocompleteApplying())
            ) {
              ctx.actions.clearAddressConsistencyHint();
            }
            if (isAddressGateFieldName(name)) {
              if (name !== 'attributes[recipient_postal]') onChange();
              return;
            }
            validateAndBlockCheckout();
            saveStateSnapshot();
          });
          on(inp, 'blur', function () {
            var name = String((inp && (inp.name || (inp.getAttribute && inp.getAttribute('name')))) || '');
            if (!isAddressGateFieldName(name) || name === 'attributes[recipient_postal]') return;
            if (name === 'attributes[recipient_address1]') {
              if (ctx.actions && typeof ctx.actions.scheduleAddressConsistencyCheck === 'function') {
                ctx.actions.scheduleAddressConsistencyCheck();
              }
            }
            onChange();
          });
        });
      }

      ['wf-ld-recipient-phone', 'wf-ld-sender-phone'].forEach(function (id) {
        var inp = getEl(id);
        on(inp, 'input', function () {
          if (ctx.actions && typeof ctx.actions.syncPhoneHidden === 'function') {
            ctx.actions.syncPhoneHidden();
          }
          validateAndBlockCheckout();
          saveStateSnapshot();
        });
      });

      ['wf-ld-recipient-phone-code', 'wf-ld-sender-phone-code'].forEach(function (id) {
        var sel = getEl(id);
        on(sel, 'change', function () {
          if (ctx.actions && typeof ctx.actions.syncPhoneHidden === 'function') {
            ctx.actions.syncPhoneHidden();
          }
          validateAndBlockCheckout();
          saveStateSnapshot();
        });
      });

      var anonCheckbox = getEl('wf-ld-anonymous');
      if (anonCheckbox) {
        on(anonCheckbox, 'change', function () {
          if (ctx.actions && typeof ctx.actions.updateAttributes === 'function') {
            ctx.actions.updateAttributes();
          }
          saveStateSnapshot();
        });
      }

      // Stale-tab guard: when customers return to an old cart tab, re-fetch
      // slot availability for the selected date before they continue.
      on(document, 'visibilitychange', function () {
        if (document.visibilityState === 'visible') scheduleSelectedDateAvailabilityRefresh();
      });
      on(window, 'focus', function () {
        scheduleSelectedDateAvailabilityRefresh();
      });
    }

    function dispose() {
      if (dateHintTimer) {
        clearTimeout(dateHintTimer);
        dateHintTimer = null;
      }
      while (disposers.length > 0) {
        var disposeFn = disposers.pop();
        try {
          disposeFn();
        } catch (err) {}
      }
      if (ctx.actions && typeof ctx.actions.onDispose === 'function') {
        ctx.actions.onDispose();
      }
    }

    return {
      bindMethodCards: bindMethodCards,
      bindTimeslotTypeCards: bindTimeslotTypeCards,
      bindFormInputs: bindFormInputs,
      syncMethodCards: syncCardActive,
      updateTimeslotTypeVisibility: updateTimeslotTypeVisibility,
      updateTimeslotLockState: updateTimeslotLockState,
      switchTimeslotType: switchTimeslotType,
      refreshAdvanceBookingState: refreshAdvanceBookingState,
      syncFeeStateAfterTimeslotRefresh: syncFeeStateAfterTimeslotRefresh,
      dispose: dispose,
    };
  }

  window.WF_LD_CART_UI = {
    createUiController: createUiController,
  };
})();
